import { GridApi } from 'ag-grid-community';
import {
  SetFilterValues,
  SimpleTextFilterValue
} from '../../grid/types/filter.types';
import { FilterType } from '../../grid/utils/filter/filter.utils';
import {
  CalendarGridColumnName,
  CalendarGridFlexColumnName
} from '../../grid/types/column.types';
import {
  CalendarFiltersState,
  CalendarFilter
} from '../../state/calendar-filter/state';
import {
  SyndicateMember,
  DealVersion,
  IssueType
} from '../../types/deal.types';
import { FilterResultFacade } from './filter-result-facade';
import { CheckboxOption, RadioOption } from '../../grid/types/option.types';
import { SetFilterModel } from 'ag-grid-enterprise';
import { FilterChange, FilterClear } from '../../state/calendar-filter/actions';
import { Observable } from 'rxjs';

export interface FilterOptions {
  year: number;
  maxPar: number;
  states: SetFilterValues;
  sectors: SetFilterValues;
  purpose: SetFilterValues;
  enhancements: string[];
  managers: string[];
  taxStatus: SimpleTextFilterValue;
  type: SimpleTextFilterValue;
  offeringType?: SimpleTextFilterValue;
  pricingStatuses: SimpleTextFilterValue;

  esgCertification: any;
  combinedDocuments: any;
  erp: SimpleTextFilterValue;
  bankQualified: SimpleTextFilterValue;
  rop: SimpleTextFilterValue;
  sourceOfRepayment: SetFilterValues;
  issueType: SetFilterValues;
  bidSubmitted: SimpleTextFilterValue;

  // advanced filters options
  syndicates: SyndicateMember[];
  subordinates: string[];
  financialAdvisors: string[];
  qib: SimpleTextFilterValue;
  soleMgd: SimpleTextFilterValue;
  parityPlus: SimpleTextFilterValue;
}

export interface IFilterComparator<T> {
  hasValidDiff(grid: Partial<T>, store: T): boolean;

  applyFilterOptions(options: number | string[]);
}

export interface IFilterHandler {
  name: string;
  label: string;

  isInclusiveExclusive: boolean;

  configureFilterOptions(params: ConfigureOptionsParams): void;

  getFilterOptions(filterOptions: FilterOptions): any;

  hasValidDiff(options: FilterOptions, grid: any, store: any): boolean;

  doFilterPass(params: FilterPassParams, result: FilterResultFacade): boolean;

  toStoreFilterModel(
    model: GridFilterUnionModel,
    filterOptions: FilterOptions
  ): GridFilterUnionModel;

  toGridFilterModel(
    filter: CalendarFilter,
    filterOptions: FilterOptions
  ): GridFilterUnionModel;

  recalculateExclusiveValues(
    currentFilter: CalendarFilter,
    filterOptions: FilterOptions
  ): void;

  getAppliedFiltersItems(filter: CalendarFilter): AppliedFilterItem[];

  filterClear(
    filterChange: FilterClear,
    state: CalendarFiltersState
  ): FilterChange;
}

export interface AppliedFilterItem {
  name: string;
  label: string;
  filterLabel: string;
  filterValue: string;
}

export interface FilterPassParams {
  deal: DealVersion;
  filters: CalendarFiltersState;
  options: FilterOptions;
  context: any;
}

export type FiltersResult = {
  [key in
    | CalendarGridColumnName
    | CalendarGridFlexColumnName
    | AdvancedFilterName]: boolean;
};

export interface FilterResult {
  deals: { [key: string]: DealFilterResult };
}

export interface EntityFilterResult {
  id: string;
  filters: FiltersResult;
  filterResult: boolean;
}

export interface DealFilterResult extends EntityFilterResult {
  maturities: { [key: string]: EntityFilterResult };
  series: { [key: string]: EntityFilterResult };
  filtersPassComplete: boolean;
  subfiltersPassComplete: boolean;
  totalPar: number;
  filteredPar: number;
}

export interface ConfigureOptionsParams {
  filterOptions: FilterOptions;
  api: GridApi;
  setFilterService: any;
  weekOf: string;
}

export interface FilterModelBase {
  filterType: FilterType;
}

export interface RadioFilterModel extends FilterModelBase, RadioOption {}

export interface DateFilterModel extends FilterModelBase {
  stringModel: string[];
  isFilterActive: boolean;
}

// It is for: size, deal structure
export interface RangeFilterModel extends FilterModelBase {
  stringModel: string;
  isFilterActive: boolean;
  minValue: number;
  maxValue: number;
}

// It is for datedDate, firstCouponDate
export interface DateRangeFilterModel extends FilterModelBase {
  stringModel: string;
  isFilterActive: boolean;
  from: string;
  to: string;
}

export interface SingleDescriptionFilterModel extends FilterModelBase {
  type: DescriptionFilterType;
  filter: string;
}

export interface DoubleDescriptionFilterModel {
  condition1: SingleDescriptionFilterModel;
  condition2: SingleDescriptionFilterModel;
  operator: DoubleDescriptionFilterOperator;
}

// It is for: state, taxStatus, type, sector, manager
export interface InclusiveExclusiveFilterModel extends FilterModelBase {
  isInclusive: boolean;
  values: string[];
  storeValues: string[];
  serviceOptionSelected?: boolean;
}

export interface RatingsFilterModel extends RangeFilterModel {
  radio: string; // strings from filter popup
  ratingsRadio: string; // strings from filter popup
  enhancements: string[];
}

export interface ESGFilterModel extends FilterModelBase {
  stringModel: string;
  selectedTypes: InclusiveExclusiveFilterModel;
  selectedVerifiers: InclusiveExclusiveFilterModel;
  selectedPreQualified?: boolean;
  isFilterActive: boolean;
}

export interface CompoundBooleanFilterModel extends FilterModelBase {
  stringModel: string;
  isFilterActive: boolean;
  boolean1: boolean;
  boolean1label: string;
  boolean2: boolean;
  boolean2label: string;
}

export enum DoubleDescriptionFilterOperator {
  AND = 'AND',
  OR = 'OR'
}

export enum DescriptionFilterType {
  Equals = 'equals',
  NotEqual = 'notEqual',
  Contains = 'contains',
  NotContains = 'notContains',
  StartsWith = 'startsWith',
  EndsWith = 'endsWith'
}

export enum DoubleFilterOperator {
  AND = 'AND',
  OR = 'OR'
}

export type GridFilterUnionModel =
  | DateFilterModel
  | RangeFilterModel
  | SingleDescriptionFilterModel
  | DoubleDescriptionFilterModel
  | RatingsFilterModel
  | InclusiveExclusiveFilterModel
  | RadioFilterModel
  | SetFilterModel;

export type GridFiltersMap = {
  [key in CalendarGridColumnName]: GridFilterUnionModel;
};

export type GridFilterModel = [string, GridFilterUnionModel];

export type GridFiltersModel = GridFilterModel[];

export enum AdvancedFilterName {
  firstCall = 'firstCallDate',
  coupon = 'coupon',
  firstCouponDate = 'firstCouponDate',
  datedDate = 'datedDate',
  price = 'price',
  extraordinaryCall = 'isExtraordinaryCall',
  bankQualified = 'isBankQualified',
  rop = 'orderPeriod',
  sourceOfRepayment = 'sourceOfRepayment',
  sector = 'sector',
  purpose = 'purpose',
  issueType = 'issueType',
  financialAdvisor = 'financialAdvisor',
  syndicate = 'syndicate',
  subordinate = 'subordinate',
  maturityYear = 'maturityYear',
  offeringType = 'offeringType',
  // maturity filter only, not a filter for a deal
  maturityEnhancement = 'maturityEnhancement',
  // series level filter only
  seriesEnhancement = 'seriesEnhancement',
  qib = 'qib',
  esgCertification = 'esgCertification',
  soleMgd = 'soleMgd',
  forwardDelivery = 'forwardDelivery',
  combinedDocuments = 'combinedDocuments',
  parityPlus = 'parityPlus'
}

export interface MaturityYearFilterModel {
  future: boolean;
  year: number;
}

export enum IssueTypeShortName {
  advancedRefunding = 'Adv. Refunding'
}

export function getIssueTypeOptionLabel(value: string) {
  const foundKey = Object.keys(IssueType).find(
    (key) => IssueType[key] === value
  );
  return (foundKey && IssueTypeShortName[foundKey]) || value;
}

export enum SubordinatedLabel {
  Subord = 'Subordinated'
}

export enum StructureType {
  Subord = 'Subord',
  Senior = 'Senior'
}

export function getSubordinateOptionLabel(value: string) {
  const foundKey = Object.keys(StructureType).find(
    (key) => StructureType[key] === value
  );
  return (foundKey && SubordinatedLabel[foundKey]) || value;
}

export enum AdvancedFilterLabel {
  firstCall = 'First Call Date',
  coupon = 'Coupon',
  firstCouponDate = 'First Coupon Date',
  datedDate = 'Dated Date',
  price = 'Price',
  extraordinaryCall = 'Extraordinary Redemption Provision',
  bankQualified = 'Bank Qualified',
  rop = 'ROP',
  sourceOfRepayment = 'Source of Repayment',
  sector = 'Sector',
  purpose = 'Purpose',
  issueType = 'Issue Type',
  financialAdvisor = 'Municipal Advisor',
  syndicate = 'Syndicate',
  subordinate = 'Subordinate',
  maturityYear = 'Maturity Year',
  qib = 'QIB',
  soleMgd = 'Sole Managed',
  roadshow = 'Roadshow',
  forwardDelivery = 'Forward Delivery',
  documentsAvailable = 'Documents',
  combinedDocuments = 'Documents',
  parityPlus = 'Parity+'
}

export interface AdvancedFilterBase<T> {
  type: FilterType;
  value: T;
  label: string;
}

export interface AdvancedFilter<T, T2 = string> extends AdvancedFilterBase<T2> {
  settings: T;
}
export interface AdvancedFilterWithOptions<T, T2, T3 = string>
  extends AdvancedFilter<T2, T3> {
  options: T;
}
export interface AdvancedFilterSettings {
  hasBlank: boolean;
  hasTextSearch: boolean;
  hasDisabled?: Observable<boolean>;
  serviceOption?: AdvancedFilterChecklistServiceOption;
  buysideOnly?: boolean;
}

export interface AdvancedFilterChecklistServiceOption {
  label: string;
  change: (
    event: Event,
    options: CheckboxOption[],
    callback: (options: CheckboxOption[]) => void
  ) => void;
  isSelectAllDisabled: boolean;
  serviceOptionSelected?: boolean;
}

export interface AdvancedFiltersPopoverContext {
  isPopoverVisible$: Observable<boolean>;
  includePricingFilters: boolean;
}

export enum BidStatusFilterLabel {
  submitted = 'Bids Submitted',
  notSubmitted = 'Bids Not Submitted'
}
